import { notifications } from '@mantine/notifications';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { errorStatus } from '@/api/errors';
import type { AdminMe, TokenPair } from '@/api/types';
import i18n from '@/i18n';

import { authKeys, fetchAdminMe, revokeRefreshToken } from './api';
import { AuthContext, type AuthContextValue, type AuthStatus } from './context';
import { onSessionEnd, REFRESH_TOKEN_KEY, tokenStorage, type SessionEndReason } from './session';

const ME_STALE_TIME_MS = 5 * 60_000;
const BLOCKED_NOTIFICATION_ID = 'session-blocked';

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [hasSession, setHasSession] = useState(tokenStorage.hasSession);
  const [endReason, setEndReason] = useState<SessionEndReason | null>(null);

  const hasSessionRef = useRef(hasSession);
  useEffect(() => {
    hasSessionRef.current = hasSession;
  }, [hasSession]);

  const meQuery = useQuery({
    queryKey: authKeys.me,
    queryFn: ({ signal }) => fetchAdminMe(signal),
    enabled: hasSession,
    staleTime: ME_STALE_TIME_MS,
  });

  /** Local teardown shared by sign-out, forced session end and sign-out in another tab. */
  const closeSession = useCallback(
    (reason: SessionEndReason) => {
      tokenStorage.clear();
      void queryClient.cancelQueries();
      queryClient.clear();
      setEndReason(reason);
      setHasSession(false);
    },
    [queryClient],
  );

  // Forced session ends reported by the HTTP layer: refresh rejected or account blocked.
  useEffect(
    () =>
      onSessionEnd((reason) => {
        if (reason === 'blocked') {
          notifications.show({
            id: BLOCKED_NOTIFICATION_ID,
            color: 'red',
            title: i18n.t('errors:user_blocked'),
            message: i18n.t('auth:blocked'),
            autoClose: 10_000,
          });
        }
        closeSession(reason);
      }),
    [closeSession],
  );

  // A token that is still accepted by the refresh endpoint but rejected by `/admin/me/`
  // (e.g. staff rights revoked) is useless for the admin app: end the session.
  const meErrorStatus = errorStatus(meQuery.error);
  const meRejected = meErrorStatus === 401 || meErrorStatus === 403;
  useEffect(() => {
    if (meRejected && tokenStorage.hasSession()) closeSession('expired');
  }, [meRejected, closeSession]);

  // Rule 4: follow sign-in/sign-out performed in other tabs.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage) return;
      if (event.key !== null && event.key !== REFRESH_TOKEN_KEY) return;
      const signedIn = tokenStorage.hasSession();
      // Equal states mean a refresh-token rotation in another tab: nothing to do.
      if (signedIn === hasSessionRef.current) return;
      if (signedIn) {
        setEndReason(null);
        setHasSession(true);
      } else {
        void queryClient.cancelQueries();
        queryClient.clear();
        setEndReason('logout');
        setHasSession(false);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [queryClient]);

  const login = useCallback(
    (tokens: TokenPair, me?: AdminMe) => {
      notifications.hide(BLOCKED_NOTIFICATION_ID);
      tokenStorage.set(tokens);
      if (me) queryClient.setQueryData(authKeys.me, me);
      setEndReason(null);
      setHasSession(true);
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    const refresh = tokenStorage.getRefresh();
    closeSession('logout');
    if (refresh) {
      revokeRefreshToken(refresh).catch(() => {
        // Best effort: the tokens are already gone locally and expire on their own.
      });
    }
  }, [closeSession]);

  const refreshMe = useCallback(async () => {
    await queryClient.refetchQueries({ queryKey: authKeys.me });
  }, [queryClient]);

  let status: AuthStatus;
  if (!hasSession) status = 'anonymous';
  else if (meQuery.data) status = 'authenticated';
  else if (meQuery.isError && !meRejected) status = 'error';
  else status = 'loading';

  const me = hasSession ? (meQuery.data ?? null) : null;

  const value = useMemo<AuthContextValue>(
    () => ({ status, me, endReason, login, logout, refreshMe }),
    [status, me, endReason, login, logout, refreshMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
